import { useCallback, useMemo } from "react";
import { routes } from "@/router";
import { platformMapper } from "@/utils/crm.util";
import { capitalizeFirstLetter } from "@/utils/helper.util";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowRight, HiOutlineCheck } from "react-icons/hi2";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useCRMConnectionsStatus } from "@/hooks/api/crm/useCRMConnectionsStatus";
import { useCRMConnectionListStatus } from "@/hooks/api/list/crm/useCRMConnectionListStatus";
import Button from "@/components/atoms/Button";
import Spinner from "@/components/atoms/Spinner";
import FeaturedText from "@/components/molecules/FeaturedText";

const CRMSuccessPage = () => {
  const { t } = useTranslation();
  const [queryParams] = useSearchParams();
  const navigate = useNavigate();

  const platform = useMemo(() => platformMapper(queryParams.get("platform")), [queryParams]);
  const listId = useMemo(() => queryParams.get("list_id"), [queryParams]);

  const { data: listCRMConnectionStatus, isLoading: isLoadingListCRMStatus } = useCRMConnectionListStatus({
    platform: platform,
    id: listId,
    refetch: true,
  });

  const { data: CRMConnections, isLoading: isLoadingCRMConnections } = useCRMConnectionsStatus({
    platform,
    refetch: true,
  });

  const confirm = useCallback(() => {
    if (listId) {
      navigate(`/lists/${listId}`, { replace: true });
    } else {
      navigate(routes.lists, { replace: true });
    }
  }, [navigate, listId]);

  if (!platform) {
    return (
      <div className={"flex flex-grow flex-col items-center justify-center gap-3"}>
        <Spinner size={"lg"} />
        <div className={"grid max-w-80 justify-center gap-2 text-center"}>
          <h2 className="text-xl font-semibold text-primary">{t("crm.connections.error.title")}</h2>
          <p className="break-words text-sm text-tertiary">{t("crm.connections.error.description")}</p>
        </div>
      </div>
    );
  }

  const showLoading = useMemo(() => {
    if (isLoadingListCRMStatus || isLoadingCRMConnections) {
      return true;
    }
    // List connect
    if (listId) {
      return !(listCRMConnectionStatus?.hubspot.connected && CRMConnections?.hubspot.connected);
    }

    // Only setup
    return !CRMConnections?.hubspot.connected;
  }, [listId, isLoadingListCRMStatus, isLoadingCRMConnections, listCRMConnectionStatus, CRMConnections]);

  if (showLoading) {
    return (
      <div className={"flex flex-grow flex-col items-center justify-center gap-3"}>
        <Spinner size={"lg"} />
        <div className={"grid max-w-80 justify-center gap-2 text-center"}>
          <h2 className="text-xl font-semibold text-primary">{t("crm.connections.loading.heading")}</h2>
          <p className="break-words text-sm text-tertiary">{t("crm.connections.loading.description")}</p>
        </div>
      </div>
    );
  }

  return (
    <FeaturedText
      featuredIcon={{ icon: HiOutlineCheck, color: "success", size: "xl" }}
      heading={t(`crm.connections.${listId ? "success" : "successPlatform"}.title`, {
        platform: capitalizeFirstLetter(platform),
      })}
      body={t(`crm.connections.${listId ? "success" : "successPlatform"}.description`)}
    >
      <div className={"pt-3"}>
        <Button
          label={t(`crm.connections.${listId ? "success" : "successPlatform"}.button`)}
          onClick={() => confirm()}
          trailingIcon={<HiOutlineArrowRight className={"size-5"} />}
        />
      </div>
    </FeaturedText>
  );
};

export default CRMSuccessPage;
